// Minimal HTML sanitizer for admin-authored content_html modules.
//
// The Phase 2 RichTextEditor produces a small, known set of tags. This
// sanitizer is a strict allow-list parse-then-rewrite, NOT a string regex
// hack: it walks the DOM and removes any element or attribute that isn't
// explicitly permitted.
//
// Threat model: an admin pastes HTML from elsewhere (Word, a webpage, etc.)
// that contains <script>, onclick handlers, javascript: URLs, or other XSS
// vectors, and the portal renders it to a client. We accept rich text
// formatting from admins but never trust the HTML byte-for-byte at render
// time.

const ALLOWED_TAGS = new Set([
  'p', 'br', 'div', 'span',
  'h2', 'h3', 'h4',
  'strong', 'b', 'em', 'i', 'u',
  'ul', 'ol', 'li',
  'a',
  'blockquote',
]);

// Attributes allowed on ANY tag. `href` is also allowed but gets extra
// per-attribute validation below (protocol allow-list).
const ALLOWED_ATTRS_ANY = new Set([
  // intentionally empty — no class/id/style allowed
]);

// Per-tag attribute allow-list. Everything else is stripped.
const ALLOWED_ATTRS_PER_TAG = {
  a: new Set(['href', 'target', 'rel']),
};

// URL protocols we allow on href. Notably: NO javascript:, NO data:, NO
// vbscript:, NO file:. Relative URLs (no protocol) are fine.
const SAFE_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);

function isSafeHref(value) {
  if (!value) return false;
  const trimmed = String(value).trim();
  // Relative URLs (no scheme) are safe by definition.
  if (!/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return true;
  try {
    // Parse against a base so relative URLs resolve fine; the protocol
    // we check is the parsed one.
    const parsed = new URL(trimmed, 'https://example.invalid/');
    return SAFE_PROTOCOLS.has(parsed.protocol);
  } catch {
    return false;
  }
}

function cleanNode(node) {
  // Iterate a snapshot of children since we may remove some during the walk.
  const children = Array.from(node.children);
  for (const child of children) {
    const tag = child.tagName.toLowerCase();

    if (!ALLOWED_TAGS.has(tag)) {
      // Replace the disallowed element with its plain text content. This
      // preserves user-visible words while wiping the dangerous wrapper.
      const text = document.createTextNode(child.textContent || '');
      child.replaceWith(text);
      continue;
    }

    const tagAllowed = ALLOWED_ATTRS_PER_TAG[tag] || new Set();
    for (const attr of Array.from(child.attributes)) {
      const name = attr.name.toLowerCase();
      if (!ALLOWED_ATTRS_ANY.has(name) && !tagAllowed.has(name)) {
        child.removeAttribute(attr.name);
        continue;
      }
      if (name === 'href') {
        if (!isSafeHref(attr.value)) {
          child.removeAttribute(attr.name);
        }
      }
    }

    // Force safe link behavior on anchors that survived.
    if (tag === 'a' && child.hasAttribute('href')) {
      child.setAttribute('target', '_blank');
      child.setAttribute('rel', 'noopener noreferrer');
    }

    cleanNode(child);
  }
}

export function sanitizeHtml(dirty) {
  if (!dirty || typeof dirty !== 'string') return '';
  const doc = new DOMParser().parseFromString(dirty, 'text/html');
  cleanNode(doc.body);
  return doc.body.innerHTML;
}
